import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import "./allpages.css";
import Header from "../../../layouts/doctor/DoctorHeader";
import { useOutletContext, useParams } from "react-router-dom";
import useEncryption from "../../../services/useEncryption";
import {
  getUserProfileApi,
  updateProfileApi,
} from "../../../api/userApi/userApi";
import { toast } from "react-toastify";
import path from "../../../path";
import { useDispatch, useSelector } from "react-redux";
import { getProfile, selectAuth } from "../../../redux/slice/authSlice";
import { createAuditLogs } from "../../../api/providerApi/providerApi";
import useHeaders from "../../../helpers/useHeaders";

export const ProfileStaff = () => {
  const { encryptData, decryptData } = useEncryption();
  const [profilePic, setProfilePic] = useState(null);
  const [profilePicError, setProfilePicError] = useState("");
  const { id } = useParams();
  const decryptedId = decryptData(decodeURIComponent(id));
  const [loading, setLoading] = useOutletContext();
  const dispatch = useDispatch();
  const { userProfile } = useSelector(selectAuth);
  const { role } = useSelector(selectAuth);
  const decryptProfile = decryptData(userProfile);
  const headers = useHeaders();

  const validateImage = (file) => {
    return new Promise((resolve, reject) => {
      if (file.size > 1024 * 1024) {
        // Check file size (1 MB)
        reject("File size should not be more than 1 MB");
        return;
      }

      const img = new Image();
      const reader = new FileReader();

      reader.onload = () => {
        img.src = reader.result;
      };

      img.onload = () => {
        if (img.width !== 512 || img.height !== 512) {
          reject("Image dimensions should be 512x512 px");
        } else {
          resolve();
        }
      };

      img.onerror = () => {
        reject("Error reading image");
      };

      reader.readAsDataURL(file);
    });
  };

  // const handleImageChange = async (event) => {
  //   const file = event.target.files[0];
  //   if (file) {
  //     try {
  //       // await validateImage(file); // Validate image file
  //       const reader = new FileReader();
  //       reader.onloadend = () => {
  //         setProfilePic(reader.result);
  //         formik.setFieldValue("profilePic", file);
  //         setProfilePicError(""); // Clear error if validation passes
  //       };
  //       reader.readAsDataURL(file);
  //     } catch (error) {
  //       setProfilePicError(error);
  //       formik.setFieldValue("profilePic", null);
  //     }
  //   }
  // };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;

    img.onload = () => {
      const size = 512;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');

      const minSize = Math.min(img.width, img.height);
      const sx = (img.width - minSize) / 2;
      const sy = (img.height - minSize) / 2;

      ctx.drawImage(img, sx, sy, minSize, minSize, 0, 0, size, size);

      canvas.toBlob((blob) => {
        if (blob) {
          const croppedFile = new File(
            [blob],
            file.name.replace(/\.\w+$/, '.jpeg'),
            {
              type: 'image/jpeg',
              lastModified: Date.now(),
            }
          );

          const reader = new FileReader();
          reader.onloadend = () => {
            setProfilePic(reader.result);
            formik.setFieldValue('profilePic', croppedFile);
            formik.setFieldTouched("profilePic", true);
            setProfilePicError('');
          };
          reader.readAsDataURL(croppedFile);
        }

        // Revoke object URL to avoid memory leaks
        URL.revokeObjectURL(objectUrl);
      }, 'image/jpeg', 0.7);
    };

    img.onerror = () => {
      setProfilePicError('Invalid image file.');
      formik.setFieldValue('profilePic', null);
      URL.revokeObjectURL(objectUrl);
    };
  };


const triggerFileUpload = () => {
  formik.setFieldTouched("profilePic", true); // <-- important
  document.getElementById("file-upload").click();
};

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      userType: "",
      doctorTitle: "",
      languagesSpoken: "",
      gender: "",
      aboutMe: "",
      profilePic: null,
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("First Name is required"),
      lastName: Yup.string().required("Last Name is required"),
      email: Yup.string()
        .email("Invalid email format")
        .required("Email is required"),
      phoneNumber: Yup.string().required("Phone Number is required"),
      userType: Yup.string().required("User Type is required"),
      doctorTitle: Yup.string().nullable().optional(),
      languagesSpoken: Yup.string().required("Languages Spoken is required"),
      gender: Yup.string().required("Gender is required"),
      aboutMe: Yup.string().required("About Me is required"),
      profilePic: Yup.mixed()
          .required("Please select a photo") 
        .test(
          "fileSize",
          "File size should not be more than 1 MB",
          (value) => !value || (value && value.size <= 1024 * 1024)
        )
        .test(
          "fileType",
          "Unsupported File Format",
          (value) =>
            !value ||
            (value && ["image/jpeg", "image/png"].includes(value.type))
        ),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      const formData = new FormData();
      formData.append("firstName", values.firstName);
      formData.append("lastName", values.lastName);
      formData.append("email", values.email);
      formData.append("phoneNumber", values.phoneNumber);
      formData.append("userType", values.userType);
      formData.append("doctorTitle", values.doctorTitle);
      formData.append("languagesSpoken", values.languagesSpoken);
      formData.append("gender", values.gender);
      formData.append("aboutMe", values.aboutMe);
      if (values.profilePic) {
        formData.append("profilePic", values.profilePic);
      }

      let response = await updateProfileApi(decryptedId, formData);
      setLoading(false);
      dispatch(getProfile(decryptedId));
      let obj = {
        event: "Doctor has updated their profle.",
      };
      await createAuditLogs({ data: encryptData(obj) }, headers);
      toast.success(response.data.message);
    },
  });

  // Define the async function inside useEffect
  useEffect(() => {
    if (decryptedId) {
      dispatch(getProfile(decryptedId)); // Replace with your decrypted user ID if needed
    }
    // Ensure userProfile is defined and has the expected properties
    if (decryptProfile) {
      const imageUrl = `${path}/${decryptProfile?.photo?.split("/").pop()}`;
      // Fetch the image from the provided URL
      fetch(imageUrl).then(async (response) => {
        // Convert the image to a Blob
        const blob = await response.blob();
        // Create a File object from the Blob
        const file = new File([blob], imageUrl.split("/").pop(), {
          type: blob.type,
        });
        // Create a DataTransfer object to simulate a file being selected
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);

        setProfilePic(imageUrl);
        formik.setFieldValue("profilePic", dataTransfer.files[0]);
      });

      formik.setValues({
        firstName: decryptProfile.firstName || "",
        lastName: decryptProfile.lastName || "",
        email: decryptProfile.email || "",
        phoneNumber: decryptProfile.phoneNumber || "",
        userType: decryptProfile.role || "",
        aboutMe: decryptProfile.aboutMe || "",
        gender: decryptProfile.gender || "",
        doctorTitle: decryptProfile.doctorTitle || "",
        languagesSpoken: decryptProfile.langSpoken || "",
      });
    }
  }, [dispatch, decryptedId]);

  return (
    <div>
      <Header pageTitle="Profile" />
      <div className="cardOuter">
        <div className="cardheads">
          <div className="cardTitle">Your Profile</div>
        </div>
        <div className="cardcontents">
          <div className="uploadprofileimg">
            <div>
              <div className="profile-pic-container">
                <img
                  src={
                    profilePic ? profilePic : "/profile.svg"
                  }
                  alt="Profile"
                  className="profile-pic"
                  style={{
                    width: "150px",
                    height: "150px",
                    borderRadius: "50%",
                  }}
                // onError={(e) =>
                //   (e.target.src = "https://via.placeholder.com/100")
                // }
                />
              </div>
              <input
                type="file"
                id="file-upload"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageChange}
              />
              <button onClick={triggerFileUpload} className="upload-button">
                Upload Image
              </button>
             {profilePicError && (
                <div style={{ color: "red" }}>{profilePicError}</div>
              )}

              {formik.touched.profilePic && formik.errors.profilePic && (
                <div style={{ color: "red" }}>{formik.errors.profilePic}</div>
              )}
            </div>
            <div className="textsides">
              <h6>Update Profile Image</h6>
              <p>The Image should be 512*512 px not bigger than 1 MB</p>
            </div>
          </div>
          <form className="sectionalForms" onSubmit={formik.handleSubmit}>
            <div className="row">
              <div className="col-lg-6 col-sm-12">
                <div className="formdesign">
                  <TextField
                    id="firstName"
                    name="firstName"
                    label="First Name"
                    className="w-100"
                    variant="outlined"
                    size="small"
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.firstName &&
                      Boolean(formik.errors.firstName)
                    }
                    helperText={
                      formik.touched.firstName && formik.errors.firstName
                    }
                  />
                </div>
              </div>
              <div className="col-lg-6 col-sm-12">
                <div className="formdesign">
                  <TextField
                    id="lastName"
                    name="lastName"
                    label="Last Name"
                    className="w-100"
                    variant="outlined"
                    size="small"
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.lastName && Boolean(formik.errors.lastName)
                    }
                    helperText={
                      formik.touched.lastName && formik.errors.lastName
                    }
                  />
                </div>
              </div>
              <div className="col-lg-6 col-sm-12">
                <div className="formdesign">
                  <TextField
                    id="email"
                    name="email"
                    label="Email"
                    className="w-100"
                    variant="outlined"
                    size="small"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                  />
                </div>
              </div>
              <div className="col-lg-6 col-sm-12">
                <div className="formdesign">
                  <TextField
                    id="phoneNumber"
                    name="phoneNumber"
                    label="Phone Number"
                    className="w-100"
                    variant="outlined"
                    size="small"
                    value={formik.values.phoneNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.phoneNumber &&
                      Boolean(formik.errors.phoneNumber)
                    }
                    helperText={
                      formik.touched.phoneNumber && formik.errors.phoneNumber
                    }
                  />
                </div>
              </div>
              <div className="col-lg-6 col-sm-12">
                <div className="formdesign">
                  <FormControl fullWidth size="small">
                    <InputLabel id="userType-label">User Type</InputLabel>
                    <Select
                      labelId="userType-label"
                      id="userType"
                      name="userType"
                      label="User Type"
                      value={formik.values.userType}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.userType &&
                        Boolean(formik.errors.userType)
                      }
                      disabled
                    >
                      {/* <MenuItem value="">Select User Type</MenuItem> */}
                      <MenuItem value="doctor">Doctor</MenuItem>
                      <MenuItem value="staff">Staff</MenuItem>
                      <MenuItem value="location">Admin</MenuItem>
                      {/* {role === "provider" && ( */}
                      <MenuItem value="provider">Location</MenuItem>
                      {/* // )} */}
                    </Select>
                  </FormControl>
                </div>
              </div>
              {formik.values.userType === "doctor" && (
                <div className="col-lg-6 col-sm-12">
                  <div className="formdesign">
                    <TextField
                      id="doctorTitle"
                      name="doctorTitle"
                      label="Doctor’s Title"
                      className="w-100"
                      variant="outlined"
                      size="small"
                      value={formik.values.doctorTitle}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.doctorTitle &&
                        Boolean(formik.errors.doctorTitle)
                      }
                      helperText={
                        formik.touched.doctorTitle && formik.errors.doctorTitle
                      }
                    />
                  </div>
                </div>
              )}
              <div className="col-lg-6 col-sm-12">
                <div className="formdesign">
                  <FormControl fullWidth size="small">
                    <InputLabel id="languagesSpoken-label">
                      Languages Spoken
                    </InputLabel>
                    <Select
                      labelId="languagesSpoken-label"
                      id="languagesSpoken"
                      name="languagesSpoken"
                      label="languages Spoken"
                      value={formik.values.languagesSpoken}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.languagesSpoken &&
                        Boolean(formik.errors.languagesSpoken)
                      }
                    >
                      <MenuItem value="">Select Language</MenuItem>
                      <MenuItem value="English">English</MenuItem>
                      <MenuItem value="Spanish">Spanish</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div className="col-lg-6 col-sm-12">
                <div className="formdesign">
                  <FormControl fullWidth size="small">
                    <InputLabel id="gender-label">Gender</InputLabel>
                    <Select
                      labelId="gender-label"
                      id="gender"
                      name="gender"
                      label="gender"
                      value={formik.values.gender}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.gender && Boolean(formik.errors.gender)
                      }
                    >
                      <MenuItem value="">Select Gender</MenuItem>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div className="col-lg-12 col-sm-12">
                <div className="formdesign">
                  <TextField
                    id="aboutMe"
                    name="aboutMe"
                    label="About Me"
                    className="w-100"
                    variant="outlined"
                    size="small"
                    placeholder="Describe Yourself"
                    multiline
                    rows={4}
                    value={formik.values.aboutMe}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.aboutMe && Boolean(formik.errors.aboutMe)
                    }
                    helperText={formik.touched.aboutMe && formik.errors.aboutMe}
                  />
                </div>
              </div>
              <div className="col-lg-12 col-sm-12">
                <div className="text-end">
                  <button
                    className="btn btn-blacked"
                    variant="contained"
                    type="submit"
                  >
                    Update Profile
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
